
import api from '../utils/api';

/**
 * Finance service for handling finance-related API calls
 */
const financeService = {
  /**
   * Get user balance
   * @returns {Promise<Object>} - User balance
   */
  getUserBalance: async () => {
    const response = await api.get('/finance/balance');
    return response.data;
  },

  /**
   * Get user transactions
   * @param {Object} options - Query options
   * @returns {Promise<Object>} - User transactions with pagination
   */
  getUserTransactions: async (options = {}) => {
    const { limit = 10, offset = 0, type } = options;
    const queryParams = new URLSearchParams();

    if (limit) queryParams.append('limit', limit);
    if (offset) queryParams.append('offset', offset);
    if (type) queryParams.append('type', type);

    const response = await api.get(`/finance/transactions?${queryParams.toString()}`);
    return response.data;
  },

  /**
   * Add funds to user balance
   * @param {number} amount - Amount to add
   * @param {string} description - Transaction description
   * @returns {Promise<Object>} - Updated user balance
   */
  addFunds: async (amount, description = 'Deposit') => {
    const response = await api.post('/finance/add-funds', { amount, description });
    return response.data;
  },

  /**
   * Calculate instance rental costs
   * @returns {Promise<Object>} - Instance rental costs
   */
  calculateInstanceRentalCosts: async () => {
    const response = await api.get('/finance/instance-costs');
    return response.data;
  },

  /**
   * Calculate message generation costs
   * @param {Object} options - Query options
   * @returns {Promise<Object>} - Message generation costs
   */
  calculateMessageGenerationCosts: async (options = {}) => {
    const { startDate, endDate } = options;
    const queryParams = new URLSearchParams();

    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);

    const response = await api.get(`/finance/message-costs?${queryParams.toString()}`);
    return response.data;
  },

  /**
   * Get financial summary for user
   * @returns {Promise<Object>} - Financial summary
   */
  getFinancialSummary: async () => {
    const response = await api.get('/finance/summary');
    return response.data;
  },

  /**
   * Get all financial data for the finance page
   * @returns {Promise<Object>} - Complete financial data
   */
  getFinancePageData: async () => {
    try {
      const response = await api.get('/frontend/finance');
      return response.data;
    } catch (error) {
      console.error('Error fetching finance page data:', error);
      throw error;
    }
  },

  /**
   * Get transactions summary for a period
   * @param {string} startDate - ISO date string
   * @param {string} endDate - ISO date string
   * @returns {Promise<Object>} - Summary with sums and counts per type
   */
  getPeriodSummary: async (startDate, endDate) => {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await api.get(`/finance/period-summary?${params.toString()}`);
    return response.data;
  }
};

export default financeService;